import { CaretRightIcon, PlusIcon } from '@phosphor-icons/react';
import { useState } from 'react';

import { Button } from 'components/button/Button';
import { Dialog } from 'components/dialog/Dialog';
import { useTranslation } from 'contexts/translation-context';

import { ConnectionForm } from './ConnectionForm';
import { UpcomingConnectionsBanner } from './UpcomingConnectionsBanner';
import { TELEMETRY_CONNECTOR_FORMS, type ConnectorFormSpec } from '../endpoints/shared.types';
import type { TelemetryParty } from '../models';

interface IAddTrackingConnectionDialogProps {
  party?: TelemetryParty;
  /** An internal user owns no party, so they have to name the connection's owner themselves. */
  requiresOwner?: boolean;
  onSaved: () => void;
}

/**
 * The tracking integrations we offer, and the form for whichever one is picked.
 *
 * One entry point rather than a button per provider: the list is the place that says which providers
 * exist — including the ones still to come — so the page's own action stays the same however many
 * connectors we add. Choosing a provider replaces the list with that provider's form.
 */
export const AddTrackingConnectionDialog = ({
  party,
  requiresOwner = false,
  onSaved,
}: IAddTrackingConnectionDialogProps) => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState<ConnectorFormSpec | null>(null);

  const close = () => {
    setIsOpen(false);
    // Reopening starts at the list, not at whatever was picked last time.
    setSelected(null);
  };

  return (
    <>
      <Button color="primary" iconLeft={<PlusIcon className="button-icon" />} onTrigger={() => setIsOpen(true)}>
        {i18n('liveTracking.addConnection')}
      </Button>

      <Dialog
        isOpen={isOpen}
        onClose={close}
        title={selected?.displayName ?? i18n('liveTracking.integrations')}
        // Dismissable while choosing a provider, not while filling its form in: a stray Escape or click
        // outside would silently discard a typed API key, and the form has its own Back button.
        isDismissable={!selected}
      >
        {selected ? (
          <ConnectionForm
            key={selected.provider}
            spec={selected}
            party={party}
            requiresOwner={requiresOwner}
            onSaved={() => {
              onSaved();
              close();
            }}
            onCancel={() => setSelected(null)}
          />
        ) : (
          <div className="grid gap-4">
            <p className="text-sm text-fg-muted">{i18n('liveTracking.integrationsHint')}</p>

            <div className="grid gap-3">
              {TELEMETRY_CONNECTOR_FORMS.map((spec) => (
                <button
                  key={spec.provider}
                  type="button"
                  onClick={() => setSelected(spec)}
                  className="card flex items-center justify-between gap-3 p-4 text-left transition-colors hover:border-border-strong"
                  data-clickable="true"
                >
                  <span className="min-w-0">
                    <span className="card-heading-text block">{spec.displayName}</span>
                    <span className="block text-sm text-fg-muted">{i18n(spec.descriptionKey)}</span>
                  </span>
                  <CaretRightIcon className="w-4 h-4 shrink-0 text-fg-muted" />
                </button>
              ))}
            </div>

            <UpcomingConnectionsBanner />

            <div className="flex justify-end">
              <Button onTrigger={close}>{i18n('cancel')}</Button>
            </div>
          </div>
        )}
      </Dialog>
    </>
  );
};
